import { useCallback, useContext, useEffect, useRef, useState } from "react";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import UserContext from "common/Context/UserContext";
import { useEffectiveAccess } from "common/Context/EffectiveAccessContext";
import { hasDataManagementAccess } from "common/Utilities/EffectiveAccess.utils";
import { NODE_CONTEXT_IMPORT } from "./NodeContextImportConstants";
import Toast from "common/Components/Toast/Toast";
import Loader from "common/Components/Loader";
import {
  postContextImportValidate,
  postContextImportJob,
  getContextImportJob,
  CONTEXT_IMPORT_MAX_BYTES,
  CONTEXT_IMPORT_JOB_POLL_INTERVAL_MS,
  CONTEXT_IMPORT_JOB_ACTIVE_STATES,
} from "./ContextImportApi";
import {
  buildImportToastFailure,
  buildImportToastSuccess,
  hideImportToast,
  IMPORT_TOAST_DEFAULT_DURATION,
} from "./ImportContextToast";
import {
  ImportsPageRoot,
  ImportPrimaryCard,
  ImportStatusCard,
  ImportStatusCardBody,
  CardHeader,
  CardBody,
  CardTitle,
  RunCountBadge,
  FieldLabel,
  FileRow,
  ActionRow,
  FileNameBox,
  UploadButton,
  ValidateButton,
  ClearLink,
  StatusBody,
  ScreenReaderAlert,
} from "./NodeContextImports.styles";
import {
  isValidNodeContextJsonDocument,
  NODE_CONTEXT_JSON_SHAPE_ERROR,
} from "./NodeContextFileValidation";
import {
  extractContextImportJobId,
  formatValidateApiResponseToStatus,
  formatJobApiResponseToStatus,
  extractContextImportJobState,
} from "./ContextImportResponseFormat";
import { extractNodesFromDocument } from "./ContextImportNodeLookup";
import {
  buildValidationResult,
  buildRunSummaryLine,
  buildValidAnnouncement,
  buildCompleteAnnouncement,
  VALIDATION_VERDICT,
} from "./ContextImportValidationResult";
import ValidationRunDetails from "./ValidationRunDetails";

const ACCEPT_EXT = /\.(txt|json)$/i;
let runIdCounter = 0;

function nextRunId() {
  runIdCounter += 1;
  return `import-run-${runIdCounter}`;
}

function isAllowedExtension(name) {
  return ACCEPT_EXT.test(name || "");
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const { internal } = NODE_CONTEXT_IMPORT;
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error(internal.readFileError));
    reader.readAsText(file);
  });
}

function readFileAsArrayBuffer(file) {
  if (typeof file.arrayBuffer === "function") {
    return file.arrayBuffer();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const { internal } = NODE_CONTEXT_IMPORT;
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(internal.readFileError));
    reader.readAsArrayBuffer(file);
  });
}

function resetFileInput(inputRef) {
  if (inputRef.current) inputRef.current.value = "";
}

function fileFromStoredPayload(payload) {
  if (!payload) return null;
  return new File([payload.buffer], payload.name, { type: payload.type });
}

function parseNodesFromText(text) {
  try {
    const parsed = JSON.parse(text);
    return extractNodesFromDocument(parsed);
  } catch {
    return [];
  }
}

function axiosErrorMessage(err) {
  const { requestFailed } = NODE_CONTEXT_IMPORT.serverMessages;
  const d = err?.response?.data;
  if (typeof d === "string") return d;
  if (d && typeof d.message === "string") return d.message;
  if (d && typeof d.detail === "string") return d.detail;
  if (d && typeof d.error === "string") return d.error;
  return err?.message || requestFailed;
}

function chipForRun(verdict, phase = "validation") {
  const chips = NODE_CONTEXT_IMPORT.runChips;
  if (phase === "complete") return chips.complete;
  if (phase === "failed") return chips.failed;
  if (verdict === VALIDATION_VERDICT.VALID) return chips.valid;
  return chips.failed;
}

function toneForRun(verdict, phase = "validation") {
  if (phase === "complete") return "ok";
  if (phase === "failed") return "crit";
  if (verdict === VALIDATION_VERDICT.VALID) return "ok";
  return "crit";
}

function runShouldExpand(validationResult) {
  const verdict = validationResult?.verdict;
  return (
    verdict === VALIDATION_VERDICT.FAILED ||
    verdict === VALIDATION_VERDICT.REFUSED
  );
}

function createRunFromValidation({ fileName, formatted, refusedReason = null }) {
  const validationResult =
    formatted.validationResult ??
    buildValidationResult({ fileName, refusedReason });
  const verdict = validationResult.verdict;
  return {
    id: nextRunId(),
    file: fileName,
    chip: chipForRun(verdict),
    tone: toneForRun(verdict),
    summaryLine: buildRunSummaryLine(validationResult),
    validationResult,
    expanded: runShouldExpand(validationResult),
    phase: "validation",
  };
}

export default function NodeContextImports() {
  const nci = NODE_CONTEXT_IMPORT;
  const { userProfile } = useContext(UserContext);
  const profile = userProfile?.profile ?? {};
  const { canAccessPage, usesEffectiveAccess } = useEffectiveAccess();
  const canAccessDataManagement = hasDataManagementAccess(profile, {
    usesEffectiveAccess,
    canAccessPage,
  });

  const inputRef = useRef(null);
  const filePayloadRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  const activeRunIdRef = useRef(null);
  const [activeValidationRunId, setActiveValidationRunId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [canUpload, setCanUpload] = useState(false);
  const [validating, setValidating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);
  const [statusTier, setStatusTier] = useState("neutral");
  const [statusMessage, setStatusMessage] = useState(
    canAccessDataManagement
      ? nci.sessionStatus.neutral
      : nci.sessionStatus.accessRestricted
  );
  const [importRuns, setImportRuns] = useState([]);
  const [toast, setToast] = useState(hideImportToast());
  const [screenReaderAlert, setScreenReaderAlert] = useState("");

  const showToast = useCallback((partial) => {
    setToast((prev) => ({ ...prev, ...partial }));
  }, []);

  const resetSessionStatus = useCallback(() => {
    setCanUpload(false);
    setStatusTier("neutral");
    setImportRuns([]);
    activeRunIdRef.current = null;
    setActiveValidationRunId(null);
    setScreenReaderAlert("");
    const s = NODE_CONTEXT_IMPORT.sessionStatus;
    setStatusMessage(
      canAccessDataManagement ? s.neutral : s.accessRestricted
    );
  }, [canAccessDataManagement]);

  const prependRun = useCallback((newRun) => {
    setImportRuns((prev) => [
      { ...newRun, expanded: newRun.expanded ?? runShouldExpand(newRun.validationResult) },
      ...prev.map((run) => ({ ...run, expanded: false })),
    ]);
    activeRunIdRef.current = newRun.id;
    setActiveValidationRunId(newRun.id);
  }, []);

  const updateActiveRun = useCallback((updater, { collapseOthers = false } = {}) => {
    const runId = activeRunIdRef.current;
    if (!runId) return;
    setImportRuns((prev) =>
      prev.map((run) => {
        if (run.id === runId) {
          return updater(run);
        }
        return collapseOthers ? { ...run, expanded: false } : run;
      })
    );
  }, []);

  const applyClientValidationFailure = useCallback(
    (message, fileName) => {
      setCanUpload(false);
      setStatusTier("error");
      setStatusMessage(message);
      if (fileName) {
        const formatted = formatValidateApiResponseToStatus(null, {
          fileName,
          refusedReason: message,
          uploadedNodes: [],
        });
        const run = createRunFromValidation({
          fileName,
          formatted,
          refusedReason: message,
        });
        prependRun(run);
        setScreenReaderAlert(`Validation failed for ${fileName}. ${message}`);
      }
      showToast(buildImportToastFailure(message));
    },
    [prependRun, showToast]
  );

  const runServerValidate = useCallback(async () => {
    const payload = filePayloadRef.current;
    const file = fileFromStoredPayload(payload);
    if (!file) return;
    setValidating(true);
    setToast(hideImportToast());
    setCanUpload(false);
    setScreenReaderAlert("");
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const { data } = await postContextImportValidate(fd);
      const formatted = formatValidateApiResponseToStatus(data, {
        uploadedNodes: payload?.nodes ?? [],
        fileName: file.name,
      });
      setStatusTier(formatted.tier);
      setCanUpload(formatted.canUpload);

      const run = createRunFromValidation({
        fileName: file.name,
        formatted,
      });
      prependRun(run);

      if (formatted.verdict === VALIDATION_VERDICT.FAILED) {
        setScreenReaderAlert(
          `Validation failed for ${file.name}. ${run.summaryLine}`
        );
        showToast(buildImportToastFailure(nci.toast.validationDidNotPass));
      } else {
        setScreenReaderAlert(
          buildValidAnnouncement(file.name, run.validationResult)
        );
        showToast(buildImportToastSuccess(nci.toast.validationCompleted));
      }
    } catch (err) {
      setCanUpload(false);
      const msg = axiosErrorMessage(err);
      setStatusTier("error");
      setStatusMessage(`${nci.serverMessages.validationFailedPrefix}${msg}`);
      const formatted = formatValidateApiResponseToStatus(null, {
        fileName: file.name,
        refusedReason: `${nci.serverMessages.validationFailedPrefix}${msg}`,
        uploadedNodes: payload?.nodes ?? [],
      });
      const run = createRunFromValidation({
        fileName: file.name,
        formatted,
        refusedReason: msg,
      });
      prependRun(run);
      setScreenReaderAlert(`Validation failed for ${file.name}. ${msg}`);
      showToast(buildImportToastFailure(msg));
    } finally {
      setValidating(false);
    }
  }, [nci, prependRun, showToast]);

  const handleFileChosen = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setToast(hideImportToast());
      if (!canAccessDataManagement) return;

      if (!isAllowedExtension(file.name)) {
        applyClientValidationFailure(
          nci.clientValidation.wrongExtension,
          file.name
        );
        filePayloadRef.current = null;
        setSelectedFile(null);
        setCanUpload(false);
        resetFileInput(inputRef);
        return;
      }
      if (file.size > CONTEXT_IMPORT_MAX_BYTES) {
        applyClientValidationFailure(
          nci.clientValidation.fileTooLarge,
          file.name
        );
        filePayloadRef.current = null;
        setSelectedFile(null);
        setCanUpload(false);
        resetFileInput(inputRef);
        return;
      }

      let uploadedNodes = [];

      if (/\.json$/i.test(file.name)) {
        try {
          const fileText = await readFileAsText(file);
          const parsed = JSON.parse(fileText);
          if (!isValidNodeContextJsonDocument(parsed)) {
            applyClientValidationFailure(
              NODE_CONTEXT_JSON_SHAPE_ERROR,
              file.name
            );
            filePayloadRef.current = null;
            setSelectedFile(null);
            setCanUpload(false);
            resetFileInput(inputRef);
            return;
          }
          uploadedNodes = extractNodesFromDocument(parsed);
        } catch {
          applyClientValidationFailure(
            nci.clientValidation.invalidJson,
            file.name
          );
          filePayloadRef.current = null;
          setSelectedFile(null);
          setCanUpload(false);
          resetFileInput(inputRef);
          return;
        }
      } else {
        try {
          const fileText = await readFileAsText(file);
          uploadedNodes = parseNodesFromText(fileText);
        } catch {
          uploadedNodes = [];
        }
      }

      filePayloadRef.current = {
        name: file.name,
        type: file.type,
        buffer: await readFileAsArrayBuffer(file),
        nodes: uploadedNodes,
      };
      setSelectedFile(file);
      setCanUpload(false);
      activeRunIdRef.current = null;
      setActiveValidationRunId(null);
      setStatusTier("neutral");
      setStatusMessage(nci.sessionStatus.neutral);
      resetFileInput(inputRef);
    },
    [applyClientValidationFailure, canAccessDataManagement, nci]
  );

  const handleClear = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    filePayloadRef.current = null;
    setSelectedFile(null);
    setActiveJobId(null);
    setCommitting(false);
    resetFileInput(inputRef);
    setToast(hideImportToast());
    resetSessionStatus();
  }, [resetSessionStatus]);

  const toggleRunExpanded = useCallback((runId) => {
    setImportRuns((prev) => {
      const target = prev.find((run) => run.id === runId);
      const willExpand = Boolean(target && !target.expanded);
      return prev.map((run) => {
        if (run.id === runId) {
          return { ...run, expanded: !run.expanded };
        }
        return willExpand ? { ...run, expanded: false } : run;
      });
    });
  }, []);

  useEffect(() => {
    if (!activeJobId) {
      return undefined;
    }

    let cancelled = false;

    const finishJob = ({ toastBuilder, toastMessage, phase, expandRun }) => {
      updateActiveRun(
        (run) => {
          const validationResult = run.validationResult;
          const isComplete = phase === "complete" && validationResult;
          return {
            ...run,
            chip: chipForRun(validationResult?.verdict, phase),
            tone: toneForRun(validationResult?.verdict, phase),
            phase,
            expanded: expandRun,
            summaryLine: isComplete
              ? buildRunSummaryLine(validationResult, "complete")
              : run.summaryLine,
            validationResult: isComplete
              ? {
                  ...validationResult,
                  resultsText: buildCompleteAnnouncement(
                    run.file,
                    validationResult
                  ),
                }
              : validationResult,
          };
        },
        { collapseOthers: expandRun }
      );
      showToast(toastBuilder(toastMessage));
      setCommitting(false);
      setActiveJobId(null);
    };

    const pollJobStatus = async () => {
      try {
        const { data } = await getContextImportJob(activeJobId);
        if (cancelled) {
          return;
        }

        const formatted = formatJobApiResponseToStatus(data);
        const stateUpper = extractContextImportJobState(data);

        if (CONTEXT_IMPORT_JOB_ACTIVE_STATES.has(stateUpper)) {
          pollTimeoutRef.current = window.setTimeout(
            pollJobStatus,
            CONTEXT_IMPORT_JOB_POLL_INTERVAL_MS
          );
          return;
        }

        if (stateUpper === "COMPLETED") {
          finishJob({
            toastBuilder: buildImportToastSuccess,
            toastMessage: nci.toast.importCompleted,
            phase: "complete",
            expandRun: false,
          });
          return;
        }

        finishJob({
          toastBuilder: buildImportToastFailure,
          toastMessage: formatted.errorMessage ?? nci.toast.importIncomplete,
          phase: "failed",
          expandRun: true,
        });
      } catch (err) {
        if (cancelled) {
          return;
        }
        const msg = axiosErrorMessage(err);
        showToast(buildImportToastFailure(msg));
        setStatusTier("error");
        setStatusMessage(`${nci.serverMessages.importFailedPrefix}${msg}`);
        updateActiveRun(
          (run) => ({
            ...run,
            chip: nci.runChips.failed,
            tone: "crit",
            phase: "failed",
            expanded: true,
          }),
          { collapseOthers: true }
        );
        setCommitting(false);
        setActiveJobId(null);
      }
    };

    pollJobStatus();

    return () => {
      cancelled = true;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
  }, [activeJobId, nci, showToast, updateActiveRun]);

  const validateDisabled =
    !selectedFile || validating || committing || !canAccessDataManagement;

  const uploadEnabled =
    canAccessDataManagement &&
    canUpload &&
    !!selectedFile &&
    !validating &&
    !committing &&
    !!activeValidationRunId;

  const handleUpload = useCallback(async () => {
    const file = fileFromStoredPayload(filePayloadRef.current);
    if (!uploadEnabled || !file) return;
    setCommitting(true);
    setStatusTier("neutral");
    setStatusMessage(nci.sessionStatus.importInProgress);
    setToast(hideImportToast());
    setActiveJobId(null);
    updateActiveRun((run) => ({
      ...run,
      phase: "upload",
    }));
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const { data } = await postContextImportJob(fd);
      const jobId = extractContextImportJobId(data);
      if (!jobId) {
        throw new Error(nci.serverMessages.requestFailed);
      }
      setActiveJobId(jobId);
    } catch (err) {
      const msg = axiosErrorMessage(err);
      showToast(buildImportToastFailure(msg));
      setStatusTier("error");
      setStatusMessage(`${nci.serverMessages.importFailedPrefix}${msg}`);
      updateActiveRun(
        (run) => ({
          ...run,
          chip: nci.runChips.failed,
          tone: "crit",
          phase: "failed",
          expanded: true,
        }),
        { collapseOthers: true }
      );
      setCommitting(false);
    }
  }, [nci, showToast, updateActiveRun, uploadEnabled]);

  const runCountLabel =
    importRuns.length === 1 ? "1 run" : `${importRuns.length} runs`;

  return (
    <ImportsPageRoot data-testid="subtab-content-imports">
      {canAccessDataManagement ? (
        <ImportPrimaryCard>
          <CardHeader>
            <CardTitle component="h2">{nci.titles.importCard}</CardTitle>
            <Loader
              show={validating || committing}
              fullScreen={false}
              size="small"
              loadingText=""
              variant="dots"
            />
          </CardHeader>
          <CardBody>
            <div>
              <FieldLabel component="div">{nci.labels.selectFile}</FieldLabel>
              <FileRow>
                <FileNameBox title={selectedFile?.name || ""}>
                  {selectedFile?.name || nci.labels.noFileSelected}
                </FileNameBox>
                <UploadButton
                  variant="outlined"
                  component="label"
                  disabled={validating || committing}
                >
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".txt,.json,application/json,text/plain"
                    hidden
                    onClick={(event) => {
                      event.currentTarget.value = "";
                    }}
                    onChange={handleFileChosen}
                  />
                  {nci.buttons.chooseFile}
                </UploadButton>
              </FileRow>
            </div>
            <ActionRow>
              <ValidateButton
                variant="contained"
                disabled={validateDisabled}
                onClick={runServerValidate}
              >
                {nci.buttons.validate}
              </ValidateButton>
              <UploadButton
                variant="outlined"
                disabled={!uploadEnabled}
                onClick={handleUpload}
                startIcon={
                  <FileUploadIcon sx={{ fontSize: 15 }} aria-hidden />
                }
              >
                {nci.buttons.upload}
              </UploadButton>
              <ClearLink
                component="button"
                type="button"
                underline="none"
                onClick={handleClear}
              >
                {nci.buttons.clear}
              </ClearLink>
            </ActionRow>
          </CardBody>
        </ImportPrimaryCard>
      ) : null}

      <ImportStatusCard>
        <CardHeader>
          <CardTitle component="h2">{nci.titles.statusCard}</CardTitle>
          {importRuns.length > 0 ? (
            <RunCountBadge aria-label={`${importRuns.length} validation runs`}>
              {runCountLabel}
            </RunCountBadge>
          ) : null}
        </CardHeader>
        <ImportStatusCardBody>
          {importRuns.length > 0 ? (
            importRuns.map((run) => (
              <div key={run.id} data-testid="import-status-run">
                <ValidationRunDetails
                  run={run}
                  expanded={run.expanded}
                  onToggle={() => toggleRunExpanded(run.id)}
                />
              </div>
            ))
          ) : (
            <StatusBody data-testid="import-status-message" $tier={statusTier}>
              {statusMessage}
            </StatusBody>
          )}
        </ImportStatusCardBody>
      </ImportStatusCard>

      {screenReaderAlert ? (
        <ScreenReaderAlert role="alert" aria-live="assertive">
          {screenReaderAlert}
        </ScreenReaderAlert>
      ) : null}

      <Toast
        type={toast.type}
        title={toast.title}
        description={toast.description}
        show={toast.show}
        duration={IMPORT_TOAST_DEFAULT_DURATION}
        onClose={() => setToast(hideImportToast())}
      />
    </ImportsPageRoot>
  );
}
